import React, { useMemo, useState } from "react"

import { PageTitle, StyledTextField } from "../components/StyledElements"
import { useTagAPI } from "../services/api/hooks/useTagAPI"
import { VideoQueryType } from "../services/api/video"
import { PagedVideoSuggestions } from "../components/VideoSuggestions"
import { ReactComponent as FilterIcon } from "../assets/svg/filter.svg"
import { Box, Chip, InputAdornment, useTheme } from "@mui/material"
import { TagType } from "../services/api/tag"
import { useSearchParams } from "react-router-dom"

export const GenericVideoListPage = (constraint: VideoQueryType, title: string) => (): React.ReactElement => {
  const [searchParams, setSearchParams] = useSearchParams()
  const theme = useTheme()

  const search = searchParams.get("q") || ""

  const { tagsData } = useTagAPI({})
  const tagParam = searchParams.get("tag")
  const tag_ids = useMemo(() => tagParam?.split(",").map(Number) || [], [tagParam])
  const tagFilter = useMemo(() => tagsData.filter((tag) => tag_ids.includes(tag.id)), [tagsData, tag_ids])

  const promoParam = searchParams.get("prm")
  const promo = useMemo(() => Number(promoParam) || null, [promoParam])

  const [showFilters, setShowFilters] = useState(tag_ids.length > 0 || promo !== null)

  const addTag = (tag: TagType) => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev)
        newParams.set("tag", [...tag_ids, tag.id].join(","))
        return newParams
      },
      { replace: true },
    )
  }

  const removeTag = (id: number) => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev)
        const newTagIds = tag_ids.filter((tag_id) => tag_id != id)
        if (newTagIds.length === 0) {
          newParams.delete("tag")
        } else {
          newParams.set("tag", newTagIds.join(","))
        }
        return newParams
      },
      { replace: true },
    )
  }

  const resetTags = () => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev)
        newParams.delete("tag")
        return newParams
      },
      { replace: true },
    )
  }

  const setPromo = (value: number | null) => {
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev)
        if (value === null) {
          newParams.delete("prm")
        } else {
          newParams.set("prm", value.toString())
        }
        return newParams
      },
      { replace: true },
    )
  }

  return (
    <>
      <PageTitle>{title}</PageTitle>
      <Box padding={2}>
        <Box minHeight="50px" display="flex" flexDirection="row" alignItems="center">
          <FilterIcon
            height="30px"
            width="30px"
            style={{
              stroke: showFilters ? theme.palette.secondary.main : "grey",
              transition: ".3s",
              cursor: "pointer",
            }}
            onClick={() => {
              if (tag_ids.length > 0) resetTags()
              if (promo !== null) setPromo(null)
              setShowFilters(!showFilters)
            }}
          />
          {showFilters ? (
            <>
              <StyledTextField
                type="text"
                variant="standard"
                color="soft"
                value={promo || ""}
                onChange={(e) => setPromo(e.target.value === "" ? null : parseInt(e.target.value))}
                placeholder=""
                slotProps={{ input: { startAdornment: <InputAdornment position="start">{"P"}</InputAdornment> } }}
                sx={{ marginX: 1, maxWidth: "100px" }}
              />
              {tagsData.map((tag) => (
                <Chip
                  key={tag.id}
                  color={tag_ids.includes(tag.id) ? "secondary" : "primary"}
                  label={tag.name}
                  onClick={tag_ids.includes(tag.id) ? () => removeTag(tag.id) : () => addTag(tag)}
                  sx={{ marginX: 1 }}
                />
              ))}
            </>
          ) : (
            <></>
          )}
        </Box>
        <PagedVideoSuggestions
          constraint={constraint}
          tagFilter={tagFilter}
          promoFilter={promo !== null ? [promo % 100] : []}
          search={search}
          md={3}
          pageSize={48}
        />
      </Box>
    </>
  )
}

export const Videos = GenericVideoListPage(VideoQueryType.SEARCH, "Toutes nos vidéos")

export const MostViewedVideos = GenericVideoListPage(VideoQueryType.MOST_VIEWED, "Les plus vues")

export const MostLikedVideos = GenericVideoListPage(VideoQueryType.MOST_LIKED, "Les plus likées")

export const LikedVideos = GenericVideoListPage(VideoQueryType.LIKED, "Mes vidéos likées")

export const History = GenericVideoListPage(VideoQueryType.HISTORY, "Mon historique")
